// Tutorial definitions for Knods
// Each tutorial guides users through specific workflows

export interface TutorialAction {
  type: 'click' | 'drag' | 'type' | 'connect' | 'add-node' | 'generate' | 'generate-start' | 'generate-complete' | 'any';
  target?: string;  // CSS selector or node type
  nodeType?: string; // For add-node actions
}

export interface TutorialStep {
  id: string;
  type: 'tooltip' | 'action' | 'wait' | 'highlight' | 'celebration';
  target?: string;           // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  title?: string;
  content: string;
  copyText?: string;         // Optional text the user can copy with a button
  action?: TutorialAction;   // What user must do to proceed
  autoAdvance?: boolean;     // Auto-proceed after delay
  delay?: number;            // ms to wait before auto-advancing
  showNextButton?: boolean;  // Show "Next" button (default true for non-action steps)
  showSkipButton?: boolean;  // Show "Skip" button
}

export interface TutorialDefinition {
  id: string;
  name: string;
  description: string;
  duration: string;          // "2 min", "5 min"
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  steps: TutorialStep[];
  prerequisites?: string[];  // Tutorial IDs that should be completed first
}

export const tutorials: TutorialDefinition[] = [
  {
    id: 'first-image',
    name: 'Your First Image',
    description: 'Learn how to create your first AI-generated image in under 2 minutes.',
    duration: '2 min',
    difficulty: 'beginner',
    steps: [
      {
        id: 'welcome',
        type: 'tooltip',
        position: 'center',
        title: 'Welcome to Knods! 🎨',
        content: 'This quick tutorial will show you how to create your first AI-generated image. It only takes about 2 minutes!',
        showNextButton: true,
        autoAdvance: false,
      },
      {
        id: 'explain-canvas',
        type: 'tooltip',
        target: '.react-flow',
        position: 'center',
        title: 'The Canvas',
        content: 'This is your creative canvas. You\'ll add nodes here to build AI workflows. Nodes are like building blocks that you connect together.',
        showNextButton: true,
        autoAdvance: false,
      },
      {
        id: 'show-palette',
        type: 'action',
        target: '.sidebar-button.category-image',
        position: 'right',
        title: 'Open the Image Category',
        content: 'Click Image in the sidebar to open the Image nodes.',
        action: {
          type: 'click',
          target: '[data-tutorial="image-category"]',
        },
        showSkipButton: true,
      },
      {
        id: 'drag-image-node',
        type: 'action',
        target: '[data-tutorial="z-image-turbo-node"]',
        position: 'right',
        title: 'Add a Z-Image Turbo Node',
        content: 'Click the "Z-Image Turbo" node to add it to the canvas. (You can also drag it to place it wherever you want.)',
        action: {
          type: 'add-node',
          nodeType: 'zImageTurbo',
        },
      },
      {
        id: 'find-prompt',
        type: 'action',
        target: '.markdown-textarea-container',
        position: 'right',
        title: 'Write Your Prompt',
        content: 'Click the prompt area and describe what you want to create.',
        action: {
          type: 'click',
          target: '.markdown-textarea-container',
        },
      },
      {
        id: 'click-generate',
        type: 'action',
        target: '.text-editor-modal',
        position: 'right',
        title: 'Generate Your Image',
        content: 'Write your prompt in the editor, then click Generate! Try this one:',
        copyText: 'A glowing butterfly made of stained glass, floating over a moonlit garden, magical realism, ultra detailed',
        action: {
          type: 'generate-start',
        },
      },
      {
        id: 'generating',
        type: 'tooltip',
        target: '.react-flow__node-zImageTurbo',
        position: 'right',
        title: 'Magic is happening... ✨',
        content: 'The AI is painting your vision into existence. No pressure, it\'s only creating something from nothing.',
        showNextButton: false,
        autoAdvance: false,
        action: {
          type: 'generate-complete',
        },
      },
      {
        id: 'celebration',
        type: 'celebration',
        target: '.react-flow__node-customOutput',
        position: 'left',
        title: 'Your image is here! 🎉',
        content: 'Beautiful! You just created your first AI image. The canvas is yours now — play with prompts, add more nodes, and see what you can make.',
        showNextButton: true,
        autoAdvance: false,
      },
    ],
  },
  {
    id: 'connect-nodes',
    name: 'Connect Nodes',
    description: 'Learn how to connect nodes to create powerful AI workflows.',
    duration: '3 min',
    difficulty: 'beginner',
    prerequisites: ['first-image'],
    steps: [
      {
        id: 'intro',
        type: 'tooltip',
        position: 'center',
        title: 'Connecting Nodes 🔗',
        content: 'In this tutorial, you\'ll learn how to connect nodes together to create workflows where the output of one node flows into another.',
        showNextButton: true,
      },
      {
        id: 'add-prompt-node',
        type: 'action',
        target: '[data-tutorial="prompt-node"]',
        position: 'right',
        title: 'Add a Prompt Node',
        content: 'First, let\'s add a Prompt node. This node lets you write text that can be used by other nodes.',
        action: {
          type: 'add-node',
          nodeType: 'prompt',
        },
        showSkipButton: true,
      },
      {
        id: 'add-image-node',
        type: 'action',
        target: '[data-tutorial="image-node"]',
        position: 'right',
        title: 'Add an Image Node',
        content: 'Now add an Image node to the right of your Prompt node.',
        action: {
          type: 'add-node',
          nodeType: 'image',
        },
        showSkipButton: true,
      },
      {
        id: 'explain-handles',
        type: 'highlight',
        target: '.react-flow__handle',
        position: 'top',
        title: 'Connection Points',
        content: 'See those small circles on the edges of nodes? Those are handles. Outputs are on the right, inputs are on the left.',
        showNextButton: true,
      },
      {
        id: 'make-connection',
        type: 'action',
        target: '.react-flow__handle-right',
        position: 'top',
        title: 'Connect the Nodes',
        content: 'Drag from the right handle of the Prompt node to the left handle of the Image node to connect them.',
        action: {
          type: 'connect',
        },
        showSkipButton: true,
      },
      {
        id: 'explain-flow',
        type: 'tooltip',
        position: 'center',
        title: 'Data Flow',
        content: 'Great! Now when you write in the Prompt node and the Image node generates, it will use your prompt text automatically.',
        showNextButton: true,
      },
      {
        id: 'run-flow-intro',
        type: 'highlight',
        target: '[data-tutorial="run-flow-button"]',
        position: 'bottom',
        title: 'Run Knod',
        content: 'You can also click "Run Knod" to execute all connected nodes automatically, from left to right!',
        showNextButton: true,
      },
      {
        id: 'celebration',
        type: 'celebration',
        position: 'center',
        title: 'You\'re a Pro! 🚀',
        content: 'You now know how to connect nodes! This is the foundation of creating powerful AI workflows. Try connecting more nodes to build complex pipelines!',
        showNextButton: true,
      },
    ],
  },
  {
    id: 'image-to-video',
    name: 'Image to Video',
    description: 'Turn your AI-generated images into stunning videos.',
    duration: '4 min',
    difficulty: 'intermediate',
    prerequisites: ['first-image', 'connect-nodes'],
    steps: [
      {
        id: 'intro',
        type: 'tooltip',
        position: 'center',
        title: 'Image to Video 🎬',
        content: 'In this tutorial, you\'ll learn how to transform a static image into a dynamic video using AI.',
        showNextButton: true,
      },
      {
        id: 'add-image',
        type: 'action',
        target: '[data-tutorial="image-node"]',
        position: 'right',
        title: 'Start with an Image',
        content: 'First, add an Image node and generate an image (or use an existing one).',
        action: {
          type: 'add-node',
          nodeType: 'image',
        },
        showSkipButton: true,
      },
      {
        id: 'add-video',
        type: 'action',
        target: '[data-tutorial="video-node"]',
        position: 'right',
        title: 'Add a Video Node',
        content: 'Now add a Video node to the right of your Image node.',
        action: {
          type: 'add-node',
          nodeType: 'video',
        },
        showSkipButton: true,
      },
      {
        id: 'connect-them',
        type: 'action',
        target: '.react-flow__handle',
        position: 'top',
        title: 'Connect Image to Video',
        content: 'Connect the Image node\'s output to the Video node\'s input.',
        action: {
          type: 'connect',
        },
        showSkipButton: true,
      },
      {
        id: 'explain-video-settings',
        type: 'tooltip',
        position: 'center',
        title: 'Video Settings',
        content: 'Video nodes have settings for duration, motion style, and more. The AI will animate your image based on these settings.',
        showNextButton: true,
      },
      {
        id: 'generate-video',
        type: 'action',
        target: '.video-node button[type="submit"]',
        position: 'left',
        title: 'Generate Video',
        content: 'Click generate to create your video! Note: Videos use more credits than images.',
        action: {
          type: 'generate',
        },
        showSkipButton: true,
      },
      {
        id: 'celebration',
        type: 'celebration',
        position: 'center',
        title: 'Amazing! 🎥',
        content: 'You\'ve created an AI video! Check the Flow Assets panel to see all your generated content.',
        showNextButton: true,
      },
    ],
  },
];

export function getTutorialById(id: string): TutorialDefinition | undefined {
  return tutorials.find(t => t.id === id);
}

export function getBeginnerTutorials(): TutorialDefinition[] {
  return tutorials.filter(t => t.difficulty === 'beginner');
}
